import { importProvidersFrom } from '@angular/core';
import { FIREBASE_OPTIONS } from '@angular/fire/compat';
import { bootstrapApplication } from '@angular/platform-browser';
import { PreloadAllModules, RouteReuseStrategy, provideRouter, withPreloading } from '@angular/router';
import { IonicRouteStrategy, provideIonicAngular } from '@ionic/angular/standalone';
import { SafeArea } from 'capacitor-plugin-safe-area';
import { initializeApp } from 'firebase/app';
import { EmailAuthProvider, getAuth, indexedDBLocalPersistence, initializeAuth } from 'firebase/auth';
import { getFirestore, initializeFirestore } from 'firebase/firestore';
import { getFunctions } from 'firebase/functions';
import { getStorage } from 'firebase/storage';
import firebaseui from 'firebaseui';
import { FirebaseUIModule } from 'firebaseui-angular';
import { addIcons } from 'ionicons';
import * as allIcons from 'ionicons/icons';
import { register } from 'swiper/element/bundle';
import { AppComponent } from './app/app.component';
import { routes } from './app/app.routes';
import { environment } from './environments/environment';
import { Capacitor } from '@capacitor/core';
import { getAnalytics } from 'firebase/analytics';

const firebaseUiAuthConfig: firebaseui.auth.Config = {
  signInFlow: 'popup',
  signInOptions: [
    {
      requireDisplayName: true,
      provider: EmailAuthProvider.PROVIDER_ID,
    },
  ],
  tosUrl: '/terms',
  privacyPolicyUrl: '/privacy',
};

// Register Swiper elements globally
register();

// Initialize Firebase modular SDK
const firebaseApp = initializeApp(environment.firebase);

// We do the which function to avoid this error on iOS:
// "TypeError: undefined is not an object (evaluating 'gapi.iframes.getContext')"
// Also, we don't always use the initializeAuth because on web it's slow.
function whichAuth() {
  let auth;
  if (Capacitor.getPlatform() === 'ios') {
    auth = initializeAuth(firebaseApp, {
      persistence: indexedDBLocalPersistence,
    });
  } else {
    auth = getAuth();
  }
  return auth;
}

// Initialize Firebase compat SDK (this is enough for both compat and modular usage)
addIcons(allIcons);

bootstrapApplication(AppComponent, {
  providers: [
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    provideIonicAngular(),
    provideRouter(routes, withPreloading(PreloadAllModules)),
    { provide: 'FIREBASE_APP', useValue: firebaseApp },
    {
      provide: 'FIREBASE_AUTH',
      useValue: whichAuth(),
    },
    { provide: 'FIREBASE_FIRESTORE', useValue: initializeFirestore(firebaseApp, {}) },
    { provide: 'FIREBASE_STORAGE', useValue: getStorage(firebaseApp) },
    { provide: 'FIREBASE_FUNCTIONS', useValue: getFunctions(firebaseApp) },
    {
      provide: 'FIREBASE_ANALYTICS',
      useValue: Capacitor.getPlatform() === 'ios' ? null : getAnalytics(firebaseApp),
    },
    importProvidersFrom(FirebaseUIModule.forRoot(firebaseUiAuthConfig)),
    { provide: FIREBASE_OPTIONS, useValue: environment.firebase },
  ],
});

SafeArea.getSafeAreaInsets().then(data => {
  const { insets } = data;
  document.body.style.setProperty('--ion-safe-area-top', `${insets.top}px`);
  document.body.style.setProperty('--ion-safe-area-right', `${insets.right}px`);
  document.body.style.setProperty('--ion-safe-area-bottom', `${insets.bottom}px`);
  document.body.style.setProperty('--ion-safe-area-left', `${insets.left}px`);
});
